import React, { useState, useEffect } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, useNavigate, useParams } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { AuthProvider, useAuth } from './AuthContext';
import AuthGuard from './AuthGuard';
import SubscriptionGuard from './SubscriptionGuard';
import AuthModal from './AuthModal';
import AdaChatbot from './AdaChatbot';
import { Components } from './components';
import { 
  WorkVisaAgentFlow, 
  AsylumProtectionAgentFlow, 
  StudentVisaAgentFlow, 
  CitizenshipAgentFlow,
  DacaTpsRenewalAgentFlow,
  VisitorTouristAgentFlow
} from './AgentFlows';
import { RfeNoidResponseAgentFlow } from './RfeAgentFlow';
import { AIImmigrationExpertFlow } from './AIImmigrationExpertFlow';
import { USCISNoticeScannerFlow } from './USCISNoticeScannerFlow';
import PricingPlanSelection from './PricingPlanSelection';
import PlanSelectionSuccess from './PlanSelectionSuccess';
import FeatureAccessControl from './FeatureAccessControl';
import PricingPage from './PricingPage';
import AgentSelectionModal from './AgentSelectionModal';
import useAgentSelectionCheck from './useAgentSelectionCheck';
import useSubscriptionCheck from './useSubscriptionCheck';
import AgentAccessControl from './AgentAccessControl';
import ForAttorneys from './ForAttorneys';
import PricingSection from './PricingSection';
import SubscriptionSuccess from './SubscriptionSuccess';
import Profile from './Profile';
import TermsOfService from './TermsOfService';
import PrivacyPolicy from './PrivacyPolicy';
import Cookies from "js-cookie";

const {
  Header,
  Banner,
  Hero,
  StatsSection,
  SuccessStories,
  AIAgents,
  FeaturedTool,
  FormSupport,
  ValueProposition,
  CTASection,
  AttorneyNetwork,
  ContactUs,
  Footer,
  FamilyBasedAgentFlow
} = Components;

const Home = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showAIExpert, setShowAIExpert] = useState(false);
  const [showUSCISScanner, setShowUSCISScanner] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalMode, setAuthModalMode] = useState('signin');
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { isRequired: agentSelectionRequired, loading: agentSelectionLoading } = useAgentSelectionCheck();
  const { subscriptionData, needsSubscriptionSelection } = useSubscriptionCheck();
    

  console.log('user',user)

  // Check for auth success/error from Google OAuth redirect
  useEffect(() => {
     const urlParams = new URLSearchParams(window.location.search);
    const authStatus = urlParams.get("auth");
    const token = urlParams.get("token");

    if (authStatus === "success" && token) {
      // Store token in a cookie
      Cookies.set("session_token", token, {
        expires: 7,       // 7 days
        secure: true,     // HTTPS only in production
        sameSite: "Lax",  // Match backend setting
        path: "/",
      });

      // Remove auth params from URL
      window.history.replaceState({}, document.title, window.location.pathname);

      // Refresh to get user data
      window.location.reload();

    } else if (authStatus === "error") {
      alert("Authentication failed. Please try again.");
      window.history.replaceState({}, document.title, window.location.pathname);
    }

  }, []);

  const handleStartCaseReview = (agentType = 'family-based') => {
    // Start Free Case Review should be available to ALL users per requirements
    // No authentication required - redirect directly to case review
    navigate(`/case-review/${agentType}`);
  };

  const handleOpenAIExpert = () => {
    // Check if user is authenticated
    if (!user) {
      handleAuthAction('signin');
      return;
    }
    setShowAIExpert(true);
  };

  const handleOpenUSCISScanner = () => {
    // Check if user is authenticated
    if (!user) {
      handleAuthAction('signin');
      return;
    }
    setShowUSCISScanner(true);
  };

  const handleCloseAIExpert = () => {
    setShowAIExpert(false);
  };

  const handleCloseUSCISScanner = () => {
    setShowUSCISScanner(false);
  };

  const handleAuthAction = (mode = 'signin') => {
    if (user) {
      logout();
    } else {
      setShowAuthModal(true);
      setAuthModalMode(mode == 'signup' ? 'signup' : 'signin');
    }
  };

  const handleSignupSuccess = () => {
    // After successful signup, users should login manually
    console.log('Signup successful, user should login manually');
  };

  const handleLoginSuccess = () => {
    // After successful login, check subscription status
    console.log('Login successful, checking subscription status');
    // Trigger a page reload to ensure subscription check runs
    setTimeout(() => {
      window.location.reload();
    }, 100);
  };

  const handleAgentSelectionComplete = () => {
    // Refresh the page or update state as needed
    window.location.reload();
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <Header 
        onOpenAIExpert={handleOpenAIExpert}
        onOpenUSCISScanner={handleOpenUSCISScanner}
        user={user}
        onAuthAction={handleAuthAction}
      />
      <Banner 
        onOpenUSCISScanner={handleOpenUSCISScanner}
        onOpenAIExpert={handleOpenAIExpert}
      />
      <Hero 
        searchQuery={searchQuery} 
        setSearchQuery={setSearchQuery}
        onOpenAIExpert={handleOpenAIExpert}
        onOpenUSCISScanner={handleOpenUSCISScanner}
        user={user}
        onAuthRequired={handleAuthAction}
      />
      <StatsSection />
      <SuccessStories />
      <AIAgents 
        onStartCaseReview={handleStartCaseReview} 
        user={user}
        onAuthRequired={handleAuthAction}
      />
      <FeaturedTool />
      <PricingSection />
      <FormSupport />
      <ValueProposition />
      <CTASection 
        user={user}
        onAuthRequired={handleAuthAction}
        onOpenAIExpert={handleOpenAIExpert}
      />
      <AttorneyNetwork />
      <ContactUs />
      <Footer />
      
      {/* New Flow Modals with Feature Access Control */}
      {showAIExpert && (
        <FeatureAccessControl feature="ai_immigration_assistant">
          <AIImmigrationExpertFlow 
            isOpen={showAIExpert} 
            onClose={handleCloseAIExpert} 
          />
        </FeatureAccessControl>
      )}
      
      {showUSCISScanner && (
        <FeatureAccessControl feature="scan_uscis_notice">
          <USCISNoticeScannerFlow 
            isOpen={showUSCISScanner} 
            onClose={handleCloseUSCISScanner} 
          />
        </FeatureAccessControl>
      )}
      
      {/* Authentication Modal */}
      <AuthModal 
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        initialMode={authModalMode}
        onSignupSuccess={handleSignupSuccess}
        onLoginSuccess={handleLoginSuccess}
      />
      
      {/* Agent Selection Modal */}
      <AgentSelectionModal
        isOpen={!agentSelectionLoading && agentSelectionRequired}
        onClose={() => {}}
        onComplete={handleAgentSelectionComplete}
      />
      
      {/* Ada Emotional Support Chatbot */}
      <AdaChatbot />
    </div>
  );
};

const CaseReviewPage = () => {
  const navigate = useNavigate();
  const { agentType } = useParams();
  
  const handleClose = () => {
    navigate('/');
  };

  // Map agent types to their IDs for access control
  const getAgentId = (agentType) => {
    const agentMapping = {
      'family-based-agent': 'family-based',
      'work-visa-agent': 'work-visa', 
      'asylum---protection-agent': 'asylum-protection',
      'student---exchange-visa-agent': 'student-visa',
      'citizenship-agent': 'citizenship',
      'daca---tps-renewal-agent': 'daca-tps',
      'rfe---noid-response-agent': 'rfe-noid',
      'visitor---tourist-visa-agent': 'visitor-tourist'
    };
    return agentMapping[agentType] || 'family-based';
  };

  // Map agent types to their respective flow components
  const getAgentFlow = () => {
    const agentId = getAgentId(agentType);
    
    const flowComponent = (() => {
      switch (agentType) {
        case 'family-based-agent':
          return <FamilyBasedAgentFlow isOpen={true} onClose={handleClose} />;
        case 'work-visa-agent':
          return <WorkVisaAgentFlow isOpen={true} onClose={handleClose} />;
        case 'asylum---protection-agent':
          return <AsylumProtectionAgentFlow isOpen={true} onClose={handleClose} />;
        case 'student---exchange-visa-agent':
          return <StudentVisaAgentFlow isOpen={true} onClose={handleClose} />;
        case 'citizenship-agent':
          return <CitizenshipAgentFlow isOpen={true} onClose={handleClose} />;
        case 'daca---tps-renewal-agent':
          return <DacaTpsRenewalAgentFlow isOpen={true} onClose={handleClose} />;
        case 'rfe---noid-response-agent':
          return <RfeNoidResponseAgentFlow isOpen={true} onClose={handleClose} />;
        case 'visitor---tourist-visa-agent':
          return <VisitorTouristAgentFlow isOpen={true} onClose={handleClose} />;
        default:
          return <FamilyBasedAgentFlow isOpen={true} onClose={handleClose} />;
      }
    })();

    // Wrap the flow component with access control
    return (
      <AgentAccessControl agentId={agentId}>
        {flowComponent}
      </AgentAccessControl>
    );
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {getAgentFlow()}
    </div>
  );
};

function App() {
  return (
    <GoogleOAuthProvider clientId="66469596361-unctsi18sksfru8ikd53pkct53gsk13p.apps.googleusercontent.com">
      <AuthProvider>
        <div className="App">
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/for-attorneys" element={<ForAttorneys />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/terms-of-service" element={<TermsOfService />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/subscription/success" element={<SubscriptionSuccess />} />
              <Route path="/plan-selection/success" element={<PlanSelectionSuccess />} />
              <Route path="/profile" element={
                <SubscriptionGuard>
                  <Profile />
                </SubscriptionGuard>
              } />
              <Route path="/case-review/:agentType" element={
                <SubscriptionGuard>
                  <CaseReviewPage />
                </SubscriptionGuard>
              } />
              <Route path="*" element={<div> 404 </div>} />
            </Routes>
          </BrowserRouter>
        </div>
      </AuthProvider>
    </GoogleOAuthProvider>
  );
}

export default App;